import { type FetchError, type FetchOptions, ofetch } from 'ofetch';
import { correlationIdFromHeaders, reportApiError } from './errorReporter';
import { buildPath, DASHBOARD_PATHS } from './paths';
import type {
  DiagSession,
  HierarchySnapshot,
  PaginatedResponse,
  RunSummary,
  TestSession,
} from './types';

/** Request options for the `api`/`apiPublic` clients. Re-exported so wrapper
 *  helpers can type their pass-through options without importing ofetch
 *  directly (the ESLint ban blocks that everywhere but this file). */
export type ApiRequestOptions = FetchOptions<'json'>;

// ---------------------------------------------------------------------------
// The ONLY HTTP entry point for the dashboard. Raw `fetch` and direct `ofetch`
// imports are banned by ESLint everywhere except this file.
//
//  - `api`        → authenticated client. Injects Authorization / X-Org-Id /
//                   X-Project-Id, parses the body, THROWS a FetchError on
//                   non-2xx, logs out + redirects to /login on any 401, and
//                   forwards other failures to the admin error log.
//  - `apiPublic`  → no auth injection, no 401 redirect, no error reporting.
//                   For pre-auth flows (login/register/refresh probe / `/me`),
//                   public marketing endpoints, token-in-URL shared pages, and
//                   static assets. Still throws on non-2xx.
//  - `authHeaders` / `authJsonHeaders` → header builders for the cases that
//                   can't go through `api`: WebSocket upgrades, `<img>`/`<video>`
//                   src tokens, FormData uploads that set their own headers, and
//                   project-scoped calls that pass an explicit X-Project-Id.
//
// Return semantics: `api<T>(url, opts)` resolves to the parsed `T`. For
// endpoints whose non-2xx status carries meaning (409/410/404 branches), use
// `api.raw(url, { ignoreResponseError: true })` and read `.status` / `._data`.
// Send JSON bodies as plain objects (`body: { x }`) — ofetch serializes them
// and sets Content-Type. FormData is passed through untouched.
// ---------------------------------------------------------------------------

export const BASE = import.meta.env.VITE_API_URL || '';
const BASE_URL = BASE;

/** Auth headers (Authorization + X-Project-Id). No Content-Type. */
export function authHeaders(projectId?: string): Record<string, string> {
  const h: Record<string, string> = {};
  const token = localStorage.getItem('auth_token');
  if (token) h['Authorization'] = `Bearer ${token}`;
  const pid = projectId ?? localStorage.getItem('active_project_id') ?? '';
  if (pid) h['X-Project-Id'] = pid;
  return h;
}

/** Auth headers + `Content-Type: application/json` for JSON request bodies. */
export function authJsonHeaders(projectId?: string): Record<string, string> {
  return { ...authHeaders(projectId), 'Content-Type': 'application/json' };
}

/** Client-side routes that must render for signed-out visitors. A 401 from an
 *  authenticated `api` call while one of these is open must clear the dead
 *  token WITHOUT bouncing to /login — otherwise a background probe (e.g. the
 *  error-reporter settings fetch, mounted app-wide) yanks an invitee off
 *  /accept-invite before they can act. Mirrored by the /me handler in
 *  lib/auth.tsx, which imports this so the two lists can't drift. */
export function isPublicPagePath(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname === '/contact' ||
    pathname === '/reset-password' ||
    pathname === '/accept-invite' ||
    pathname.startsWith('/share/') ||
    pathname.startsWith('/docs')
  );
}

function clearSessionAndRedirect(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('active_org_id');
  localStorage.removeItem('active_project_id');
  // Public pages stay put — clearing the dead token is enough; ejecting a
  // signed-out visitor to /login would break invites, shared links, and docs.
  if (isPublicPagePath(window.location.pathname)) return;
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

function requestUrl(request: FetchError['request']): string {
  if (typeof request === 'string') return request;
  if (request instanceof URL) return request.toString();
  if (request instanceof Request) return request.url;
  return String(request ?? '');
}

/** The authenticated client. Injects auth headers, redirects on `/api/*` 401,
 *  reports other failures, and throws a FetchError on non-2xx. */
export const api = ofetch.create({
  baseURL: BASE,
  onRequest({ options }) {
    const headers = new Headers(options.headers as HeadersInit | undefined);
    const token = localStorage.getItem('auth_token');
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const orgId = localStorage.getItem('active_org_id');
    if (orgId && !headers.has('X-Org-Id')) headers.set('X-Org-Id', orgId);
    // An explicit empty X-Project-Id means "intentionally omit" (project-scoped
    // callers use it to bypass the active-project default); honour it by
    // deleting rather than re-injecting the localStorage value.
    if (headers.has('X-Project-Id')) {
      if (headers.get('X-Project-Id') === '') headers.delete('X-Project-Id');
    } else {
      const pid = localStorage.getItem('active_project_id');
      if (pid) headers.set('X-Project-Id', pid);
    }
    options.headers = headers;
  },
  onResponseError({ request, response, options }) {
    const url = requestUrl(request);
    const status = response.status;
    // Any 401 means the session is no longer valid — log out and redirect,
    // including expired media (`/api/files/*`) tokens.
    if (status === 401) {
      clearSessionAndRedirect();
      return;
    }
    reportApiError({
      url,
      status,
      message: (response._data as { error?: string } | undefined)?.error || response.statusText,
      method: (options.method ?? 'GET').toUpperCase(),
      requestId: correlationIdFromHeaders(response.headers),
    });
  },
});

/** Unauthenticated client — no header injection, no 401 redirect, no error
 *  reporting. For login/register/refresh probes, `/me`, public marketing
 *  endpoints, token-in-URL shared pages, and static assets. Still throws on
 *  non-2xx (callers surface the message). */
export const apiPublic = ofetch.create({ baseURL: BASE });

/** True when `e` is an ofetch FetchError (thrown on non-2xx). */
export function isFetchError(e: unknown): e is FetchError {
  return e instanceof Error && 'response' in e;
}

/** HTTP status of a thrown FetchError, or undefined for non-HTTP errors. */
export function httpStatus(e: unknown): number | undefined {
  return isFetchError(e) ? e.statusCode : undefined;
}

/** Best-effort human message from a thrown FetchError: server `error`/`message`
 *  field, else `HTTP <status>`, else the JS error message. */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (isFetchError(e)) {
    const data = e.data as { error?: string; message?: string } | undefined;
    // Some routes send { error: "<code>", message: "<sentence>" }; the code alone ("bad_request") tells nobody anything.
    if (data?.message && (!data.error || /^[a-z][a-z0-9_]*$/.test(data.error))) return data.message;
    if (data?.error) return data.error;
    if (e.statusCode) return `HTTP ${e.statusCode}`;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/** Get single session metadata */
export async function getSession(id: string): Promise<TestSession> {
  return api(buildPath(DASHBOARD_PATHS.sessionsById, { id }));
}

/** Get full session.json from the zip */
export async function getSessionJson(id: string): Promise<DiagSession> {
  return api(buildPath(DASHBOARD_PATHS.sessionsByIdSessionJson, { id }));
}

/** Build URL for a file inside the session zip (with auth token for <img>/<video> src) */
export function sessionFileUrl(id: string, fileName: string): string {
  const token = localStorage.getItem('auth_token');
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BASE_URL}${buildPath(DASHBOARD_PATHS.sessionsByIdFilesByFileName, { id, fileName: encodeURIComponent(fileName) })}${tokenParam}`;
}

/** Build URL for the session video (with auth token for <video> src) */
export function sessionVideoUrl(id: string): string {
  const token = localStorage.getItem('auth_token');
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BASE_URL}${buildPath(DASHBOARD_PATHS.sessionsByIdVideo, { id })}${tokenParam}`;
}

/** Fetch a hierarchy JSON file from the session */
export async function getHierarchy(id: string, fileName: string): Promise<HierarchySnapshot> {
  return api(
    buildPath(DASHBOARD_PATHS.sessionsByIdFilesByFileName, {
      id,
      fileName: encodeURIComponent(fileName),
    }),
  );
}

/** Fetch the video timestamp sidecar CSV. Returns null if not available. */
export async function getVideoTimestamps(
  id: string,
  fileName: string,
): Promise<Float32Array | null> {
  try {
    const text = await api(
      buildPath(DASHBOARD_PATHS.sessionsByIdFilesByFileName, {
        id,
        fileName: encodeURIComponent(fileName),
      }),
      { responseType: 'text' },
    );
    const lines = text.trim().split('\n');
    const times = new Float32Array(lines.length);
    for (let i = 0; i < lines.length; i++) {
      const comma = lines[i].indexOf(',');
      times[i] = comma >= 0 ? parseFloat(lines[i].substring(comma + 1)) : 0;
    }
    return times;
  } catch {
    return null;
  }
}

/** Get aggregate stats */
export async function getStats(): Promise<{
  total: number;
  passed: number;
  failed: number;
  warned: number;
  byProject: { project: string; count: number; failures: number }[];
}> {
  return api(DASHBOARD_PATHS.stats);
}

/** List test runs (grouped sessions) */
export async function getRuns(
  params: {
    project?: string;
    branch?: string;
    appVersion?: string;
    result?: string;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedResponse<RunSummary>> {
  return api(buildPath(DASHBOARD_PATHS.runs), {
    query: { page: 1, pageSize: 50, ...params },
  });
}

/** Delete a run and all its sessions */
export async function deleteRun(runId: string): Promise<void> {
  await api(buildPath(DASHBOARD_PATHS.runsByRunId, { runId: encodeURIComponent(runId) }), {
    method: 'DELETE',
  });
}
